/**
 * Mini-kit d'effets sonores synthétisés (Web Audio) pour la passerelle
 * d'onboarding mobile — zéro asset, zéro licence. Version réduite du kit du
 * jeu Unboarding (unboared/unboarding, src/utils/sfx.ts).
 *
 * L'AudioContext démarre souvent « suspended » (politique autoplay) : il se
 * déverrouille au premier geste utilisateur — notre tap sur le bouton arcade.
 * Sur iPhone en mode silencieux, le Web Audio est coupé par l'interrupteur :
 * le son est une couche bonus, jamais le feedback principal.
 */

let ctx: AudioContext | null = null;

function audioCtx(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!ctx) {
        const AC =
            window.AudioContext ||
            (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AC) return null;
        ctx = new AC();
    }
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    return ctx;
}

type ToneOpts = {
    freq: number;
    type?: OscillatorType;
    delay?: number; // s
    attack?: number; // s
    decay?: number; // s
    peak?: number; // gain 0..1
    slideTo?: number; // glissando de fréquence
};

function tone({ freq, type = 'sine', delay = 0, attack = 0.01, decay = 0.18, peak = 0.16, slideTo }: ToneOpts) {
    const c = audioCtx();
    if (!c) return;
    const t0 = c.currentTime + delay;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (slideTo) {
        osc.frequency.exponentialRampToValueAtTime(slideTo, t0 + attack + decay);
    }
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + attack + decay + 0.05);
}

export const onboardingSfx = {
    /** Tap sur le bouton arcade — « coin » rétro + fanfare courte. */
    press() {
        tone({ freq: 988, type: 'square', decay: 0.06, peak: 0.08 });
        tone({ freq: 1319, type: 'square', delay: 0.06, decay: 0.22, peak: 0.08 });
        const melody = [523, 659, 784, 1046]; // C5 E5 G5 C6
        melody.forEach((f, i) =>
            tone({
                freq: f,
                type: 'triangle',
                delay: 0.18 + i * 0.08,
                decay: i === melody.length - 1 ? 0.45 : 0.16,
                peak: 0.14,
            })
        );
    },

    /** Email envoyé — petit jingle montant (même esprit que la connexion). */
    sent() {
        tone({ freq: 523, type: 'triangle', peak: 0.16 }); // C5
        tone({ freq: 784, type: 'triangle', delay: 0.09, peak: 0.16 }); // G5
        tone({ freq: 1568, type: 'sine', delay: 0.18, decay: 0.3, peak: 0.09 });
    },
};
